import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import { PairPicker } from "@/components/today/PairPicker";
import { StepDots } from "@/components/today/StepDots";
import { WeeklyDigest } from "@/components/today/WeeklyDigest";
import { AccountsGrid } from "@/components/today/AccountsGrid";
import { ImportTradesModal } from "@/components/today/ImportTradesModal";
import { StatsPanel } from "@/components/StatsPanel";

const STEPS = ["pair", "direction", "lots", "prices", "pnl", "outcome", "summary", "account"];

const emptyTrade = () => ({
  pair: "",
  direction: "",
  lot_size: "",
  entry: "",
  exit: "",
  pnl: "",
  outcome: "",
  summary: "",
  account_id: null,
  account_chosen: false,
});

export default function TodayPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const todayLabel = new Date(`${today}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const [entry, setEntry] = useState(null); // existing entry
  const [phase, setPhase] = useState("gate"); // gate | trade | done
  const [gateStage, setGateStage] = useState("start"); // start | choice
  const [step, setStep] = useState(0);
  const [trade, setTrade] = useState(emptyTrade());
  const [presetAccountId, setPresetAccountId] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [streak, setStreak] = useState({ current: 0, longest: 0 });
  const [catchupDates, setCatchupDates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountsRefreshKey, setAccountsRefreshKey] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [importQueue, setImportQueue] = useState([]);
  const [importTotal, setImportTotal] = useState(0);

  const loadStreak = async () => {
    try {
      const { data } = await api.get("/journal/streak");
      setStreak(data);
      const c = await api.get("/journal/catchup/eligible");
      setCatchupDates(c.data.dates || []);
    } catch (_e) {
      console.warn("streak/catchup load failed", _e);
    }
  };

  const loadAccounts = async () => {
    try {
      const { data } = await api.get("/accounts");
      setAccounts((data || []).filter((a) => a.status !== "archived"));
      setAccountsRefreshKey((k) => k + 1);
    } catch (_e) { /* grid shows empty state */ }
  };

  const doCatchup = async (d) => {
    await api.post("/journal/catchup", { date: d });
    loadStreak();
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/journal/entries/${today}`);
      setEntry(data);
      if (data?.no_positions) setPhase("done");
      else if (data?.trades?.length > 0) setPhase("done");
      loadStreak();
      loadAccounts();
    })();
  }, [today]);

  const steps = useMemo(
    () => (presetAccountId ? STEPS.filter((s) => s !== "account") : STEPS),
    [presetAccountId]
  );

  const totalToday = useMemo(
    () => (entry?.trades || []).reduce((s, t) => s + (Number(t.pnl) || 0), 0),
    [entry]
  );

  const accountLabel = (id) => accounts.find((a) => a.id === id)?.label || null;

  const markNoPositions = async () => {
    if (entry?.trades?.length && !confirm("Today already has logged trades — marking 'no positions' will remove them. Continue?")) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post("/journal/entries", {
        date: today,
        no_positions: true,
        setup_strategy: "",
        entry_exit_reasoning: "",
        emotions: "",
        mistakes: "",
        lessons: "",
        trades: [],
        tags: [],
      });
      setEntry(data);
      setPhase("done");
      loadStreak();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const startTrade = (accountId = null) => {
    setPresetAccountId(accountId || null);
    setPhase("trade");
    setStep(0);
    setTrade({ ...emptyTrade(), account_id: accountId || null, account_chosen: !!accountId });
    setError(null);
  };

  const startImportedTrade = (t) => {
    setPresetAccountId(t.account_id || null);
    setTrade({
      ...emptyTrade(),
      pair: t.pair,
      direction: t.direction,
      lot_size: t.lot_size,
      entry: t.entry,
      exit: t.exit,
      pnl: t.pnl,
      account_id: t.account_id || null,
      account_chosen: !!t.account_id,
    });
    setPhase("trade");
    // land on outcome when fully pre-filled; otherwise on the first missing step
    let landing = STEPS.indexOf("outcome");
    if (!(parseFloat(t.lot_size) > 0)) landing = STEPS.indexOf("lots");
    else if (!t.entry || !t.exit) landing = STEPS.indexOf("prices");
    else if (t.pnl === "") landing = STEPS.indexOf("pnl");
    setStep(landing);
    setError(null);
  };

  const beginImport = (trades) => {
    if (!trades.length) return;
    setImportOpen(false);
    setImportTotal(trades.length);
    const [first, ...rest] = trades;
    setImportQueue(rest);
    startImportedTrade(first);
  };

  const cancelWizard = () => {
    setPhase(entry?.trades?.length || entry?.no_positions ? "done" : "gate");
    setGateStage("start");
    setPresetAccountId(null);
    setImportQueue([]);
    setImportTotal(0);
    setError(null);
  };

  const validateStep = () => {
    switch (steps[step]) {
      case "pair":
        return !trade.pair ? "Choose a pair to continue." : null;
      case "direction":
        return !trade.direction ? "Pick long or short." : null;
      case "lots":
        return !(parseFloat(trade.lot_size) > 0) ? "Enter a lot size greater than 0." : null;
      case "prices":
        return (!trade.entry || !trade.exit) ? "Enter both entry and exit prices." : null;
      case "pnl":
        return trade.pnl === "" ? "Enter your P&L (use negative for a loss)." : null;
      case "outcome":
        return !trade.outcome ? "Mark this trade as win, loss or breakeven." : null;
      case "account":
        return !trade.account_chosen ? "Pick an account — or choose Unassigned." : null;
      default:
        return null;
    }
  };

  const next = () => {
    const err = validateStep();
    if (err) return setError(err);
    setError(null);
    setStep((s) => Math.min(steps.length - 1, s + 1));
  };

  const back = () => {
    setError(null);
    setStep((s) => Math.max(0, s - 1));
  };

  const saveTrade = async () => {
    const err = validateStep();
    if (err) return setError(err);
    setSaving(true);
    setError(null);
    try {
      const cleanTrade = {
        pair: trade.pair,
        direction: trade.direction,
        lot_size: parseFloat(trade.lot_size) || 0,
        entry: parseFloat(trade.entry) || 0,
        exit: parseFloat(trade.exit) || 0,
        pnl: parseFloat(trade.pnl) || 0,
        outcome: trade.outcome || null,
        summary: trade.summary,
        session: trade.session || null,
        session_time: trade.session_time || null,
        strategy: trade.strategy || null,
        notes: "",
        account_id: trade.account_id || null,
      };
      const existingTrades = entry?.trades || [];
      const { data } = await api.post("/journal/entries", {
        date: today,
        no_positions: false,
        setup_strategy: entry?.setup_strategy || "",
        entry_exit_reasoning: entry?.entry_exit_reasoning || "",
        emotions: entry?.emotions || "",
        mistakes: entry?.mistakes || "",
        lessons: entry?.lessons || "",
        trades: [...existingTrades, cleanTrade],
        tags: entry?.tags || [],
      });
      setEntry(data);
      if (importQueue.length > 0) {
        const [nextT, ...rest] = importQueue;
        setImportQueue(rest);
        startImportedTrade(nextT);
      } else {
        setPhase("done");
        setTrade(emptyTrade());
        setPresetAccountId(null);
        setStep(0);
        setImportTotal(0);
      }
      loadAccounts();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetDay = async () => {
    if (!confirm("Reset today's entry? All logged trades will be removed.")) return;
    await api.delete(`/journal/entries/${today}`);
    setEntry(null);
    setPhase("gate");
    setGateStage("start");
    setStep(0);
    setTrade(emptyTrade());
    setPresetAccountId(null);
    setImportQueue([]);
    setImportTotal(0);
    loadStreak();
    loadAccounts();
  };

  const renderStep = () => {
    switch (steps[step]) {
      case "pair":
        return (
          <div>
            <p className="font-head text-2xl text-[#EBE5D9] mb-1">Which pair did you trade?</p>
            <p className="text-sm text-[#A39A8E] mb-6">
              Forex, metals, indices, crypto — pick one or type your own.
            </p>
            <PairPicker
              value={trade.pair}
              onChange={(p) => setTrade({ ...trade, pair: p })}
            />
          </div>
        );
      case "direction":
        return (
          <div>
            <p className="font-head text-2xl text-[#EBE5D9] mb-1">Long or short?</p>
            <p className="text-sm text-[#A39A8E] mb-6">
              Which direction did you take on <span className="font-mono text-[#CFA66A]">{trade.pair}</span>?
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "long", label: "LONG", desc: "Bought — expecting price to rise", tid: TID.todayDirectionLong },
                { key: "short", label: "SHORT", desc: "Sold — expecting price to fall", tid: TID.todayDirectionShort },
              ].map((o) => (
                <button
                  key={o.key}
                  data-testid={o.tid}
                  onClick={() => setTrade({ ...trade, direction: o.key })}
                  className={`p-6 border rounded-sm text-left transition-colors ${
                    trade.direction === o.key
                      ? "bg-[#CFA66A]/10 border-[#CFA66A]"
                      : "bg-[#0F0E0C] border-[#2E2A25] hover:border-[#CFA66A]"
                  }`}
                >
                  <p className="font-mono text-lg tracking-widest text-[#EBE5D9]">{o.label}</p>
                  <p className="text-xs text-[#A39A8E] mt-1">{o.desc}</p>
                </button>
              ))}
            </div>
          </div>
        );
      case "lots":
        return (
          <div>
            <p className="font-head text-2xl text-[#EBE5D9] mb-1">Lot size?</p>
            <p className="text-sm text-[#A39A8E] mb-6">
              e.g. 0.01 (micro), 0.10 (mini), 1.00 (standard)
            </p>
            <input
              data-testid={TID.todayLotSize}
              type="number"
              step="0.01"
              autoFocus
              value={trade.lot_size}
              onChange={(e) => setTrade({ ...trade, lot_size: e.target.value })}
              placeholder="0.50"
              className="w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-4 py-4 text-2xl font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
            />
          </div>
        );
      case "prices":
        return (
          <div>
            <p className="font-head text-2xl text-[#EBE5D9] mb-1">Entry &amp; exit prices</p>
            <p className="text-sm text-[#A39A8E] mb-6">
              Where you got in, and where you got out.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E]">Entry</label>
                <input
                  data-testid={TID.todayEntry}
                  type="number"
                  step="0.00001"
                  value={trade.entry}
                  onChange={(e) => setTrade({ ...trade, entry: e.target.value })}
                  className="mt-2 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-4 py-3 font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
                  placeholder="1.08500"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E]">Exit</label>
                <input
                  data-testid={TID.todayExit}
                  type="number"
                  step="0.00001"
                  value={trade.exit}
                  onChange={(e) => setTrade({ ...trade, exit: e.target.value })}
                  className="mt-2 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-4 py-3 font-mono focus:outline-none focus:border-[#CFA66A] rounded-sm"
                  placeholder="1.08850"
                />
              </div>
            </div>
          </div>
        );
      case "pnl":
        return (
          <div>
            <p className="font-head text-2xl text-[#EBE5D9] mb-1">Outcome P&amp;L</p>
            <p className="text-sm text-[#A39A8E] mb-6">
              Use a negative number for a loss (e.g. -75.50).
            </p>
            <input
              data-testid={TID.todayPnl}
              type="number"
              step="0.01"
              autoFocus
              value={trade.pnl}
              onChange={(e) => setTrade({ ...trade, pnl: e.target.value })}
              placeholder="150.00"
              className={`w-full bg-[#0F0E0C] border px-4 py-4 text-3xl font-mono focus:outline-none rounded-sm ${
                parseFloat(trade.pnl) > 0
                  ? "text-[#7A9E80] border-[#5E7A62]/60 focus:border-[#5E7A62]"
                  : parseFloat(trade.pnl) < 0
                    ? "text-[#B56258] border-[#8C4A42]/60 focus:border-[#8C4A42]"
                    : "text-[#EBE5D9] border-[#2E2A25] focus:border-[#CFA66A]"
              }`}
            />
            <p className="mt-3 font-mono text-sm text-[#A39A8E]">
              Displayed in {user?.currency || "USD"} on your calendar.
            </p>
          </div>
        );
      case "outcome":
        return (
          <div>
            <p className="font-head text-2xl text-[#EBE5D9] mb-1">Was it a win or a loss?</p>
            <p className="text-sm text-[#A39A8E] mb-6">
              How you classify it matters more than the P&amp;L sign.
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "win", label: "WIN", tid: TID.todayOutcomeWin, color: "border-[#5E7A62]/60 text-[#7A9E80]" },
                { key: "loss", label: "LOSS", tid: TID.todayOutcomeLoss, color: "border-[#8C4A42]/60 text-[#B56258]" },
                { key: "breakeven", label: "BREAK-EVEN", tid: TID.todayOutcomeBreakeven, color: "border-[#5C7C8A]/60 text-[#5C7C8A]" },
              ].map((o) => (
                <button
                  key={o.key}
                  data-testid={o.tid}
                  onClick={() => setTrade({ ...trade, outcome: o.key })}
                  className={`p-5 border rounded-sm font-mono tracking-widest text-sm transition-colors ${o.color} ${
                    trade.outcome === o.key ? "bg-[#26231F]" : "bg-[#0F0E0C] hover:bg-[#26231F]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        );
      case "summary":
        return (
          <div>
            <p className="font-head text-2xl text-[#EBE5D9] mb-1">
              Was it a good trade — and why?
            </p>
            <p className="text-sm text-[#A39A8E] mb-6">
              This is your reference for later. Talk about the setup, your execution, and what
              you&apos;d repeat or change. Honesty compounds.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Session</label>
                <select data-testid="today-session" value={trade.session || ""} onChange={(e) => setTrade({ ...trade, session: e.target.value })} className="mt-1 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm">
                  <option value="">—</option>
                  <option value="asia">Asia (Tokyo)</option>
                  <option value="london">London</option>
                  <option value="ny">New York</option>
                  <option value="custom">Custom time</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Time (24h)</label>
                <input data-testid="today-session-time" type="time" value={trade.session_time || ""} onChange={(e) => setTrade({ ...trade, session_time: e.target.value })} className="mt-1 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm" />
              </div>
            </div>
            <div className="mb-4">
              <label className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">Strategy tag (optional)</label>
              <input data-testid="today-strategy" value={trade.strategy || ""} onChange={(e) => setTrade({ ...trade, strategy: e.target.value })} placeholder="e.g. London breakout, ORB, mean reversion" className="mt-1 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-2 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-sm" />
            </div>
            <textarea
              data-testid={TID.todaySummary}
              autoFocus
              rows={5}
              value={trade.summary}
              onChange={(e) => setTrade({ ...trade, summary: e.target.value })}
              placeholder="I took this because... What went well... What I'd do differently..."
              className="w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-4 py-3 focus:outline-none focus:border-[#CFA66A] rounded-sm resize-none leading-relaxed"
            />
          </div>
        );
      case "account":
        return (
          <div>
            <p className="font-head text-2xl text-[#EBE5D9] mb-1">Assign to an account</p>
            <p className="text-sm text-[#A39A8E] mb-6">
              Tag this trade to one of your prop-firm accounts — or keep it unassigned.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                data-testid="today-account-unassigned"
                onClick={() => setTrade({ ...trade, account_id: null, account_chosen: true })}
                className={`p-5 border rounded-sm text-left transition-colors ${
                  trade.account_chosen && !trade.account_id
                    ? "bg-[#5C7C8A]/10 border-[#5C7C8A]"
                    : "bg-[#0F0E0C] border-[#2E2A25] hover:border-[#5C7C8A]"
                }`}
              >
                <p className="font-mono text-sm tracking-widest text-[#5C7C8A]">UNASSIGNED</p>
                <p className="text-xs text-[#A39A8E] mt-1">Keep it out of account analytics — assign later if you like.</p>
              </button>
              {accounts.map((a) => (
                <button
                  key={a.id}
                  data-testid={`today-account-${a.id}`}
                  onClick={() => setTrade({ ...trade, account_id: a.id, account_chosen: true })}
                  className={`p-5 border rounded-sm text-left transition-colors ${
                    trade.account_id === a.id
                      ? "bg-[#CFA66A]/10 border-[#CFA66A]"
                      : "bg-[#0F0E0C] border-[#2E2A25] hover:border-[#CFA66A]"
                  }`}
                >
                  <p className="font-mono text-sm text-[#EBE5D9] truncate">{a.label}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E] mt-1">
                    {formatMoney(a.account_size, a.currency)} &middot; {a.phase}
                  </p>
                </button>
              ))}
            </div>
            {accounts.length === 0 && (
              <p className="mt-4 font-mono text-xs text-[#A39A8E]">
                No accounts yet — create one on your Profile, or continue as Unassigned.
              </p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // ---------- Render ----------
  return (
    <Layout>
      <div className="max-w-2xl mx-auto fade-in">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">
          &mdash; {todayLabel}
        </p>
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <h1 className="font-head text-4xl sm:text-5xl font-bold text-[#EBE5D9]">
            Today&apos;s Trade
          </h1>

          <div className="flex items-center gap-3">
            <div className="border border-[#CFA66A]/40 bg-[#CFA66A]/5 px-4 py-2.5 rounded-sm">
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39A8E]">
                Current streak
              </p>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span
                  data-testid={TID.streakCurrent}
                  className="font-mono text-2xl font-medium text-[#CFA66A]"
                >
                  {streak.current}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                  {streak.current === 1 ? "day" : "days"}
                </span>
              </div>
            </div>
            <div className="border border-[#2E2A25] bg-[#1A1815] px-4 py-2.5 rounded-sm">
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39A8E]">
                Personal best
              </p>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span
                  data-testid={TID.streakLongest}
                  className="font-mono text-2xl font-medium text-[#EBE5D9]"
                >
                  {streak.longest}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                  {streak.longest === 1 ? "day" : "days"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {streak.current >= 3 && (
          <p className="mt-3 text-sm text-[#A39A8E] font-mono flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 bg-[#CFA66A] animate-pulse" />
            {streak.current === streak.longest
              ? `You're setting a personal best — ${streak.current} weekdays in a row.`
              : `${streak.current} weekdays journaled in a row. Weekends don't break your streak.`}
          </p>
        )}

        {/* At-a-glance stats — expand for the full StatsPage view */}
        <div className="mt-6">
          <StatsPanel year={new Date().getFullYear()} expandable defaultExpanded={false} />
        </div>

        {catchupDates.length > 0 && (
          <div data-testid="catchup-panel" className="mt-4 border border-[#CFA66A]/30 bg-[#CFA66A]/5 p-4 rounded-sm flex flex-wrap items-center gap-3">
            <p className="font-mono text-xs text-[#EBE5D9] flex-1 min-w-[220px]">
              You missed a weekday — one-tap catch-up preserves your streak.
            </p>
            {catchupDates.map((d) => (
              <button key={d} data-testid={`catchup-${d}`} onClick={() => doCatchup(d)} className="bg-[#CFA66A] text-[#0F0E0C] font-mono text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm hover:bg-[#EBE5D9]">
                Log {d} as no positions
              </button>
            ))}
          </div>
        )}

        <WeeklyDigest />

        {/* Accounts from Profile — expandable previews, multi-select, direct logging */}
        <AccountsGrid accounts={accounts} onLogTrade={(id) => startTrade(id)} refreshKey={accountsRefreshKey} />

        {/* GATE */}
        {phase === "gate" && (
          <div className="mt-10 border border-[#2E2A25] bg-[#1A1815] p-8 rounded-sm slide-up">
            {gateStage === "start" ? (
              <div className="text-center py-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8E]">
                  &mdash; {todayLabel}
                </p>
                <p className="font-head text-2xl text-[#EBE5D9] mt-3">Ready to log the day?</p>
                <button
                  data-testid="today-traded-btn"
                  onClick={() => setGateStage("choice")}
                  className="mt-6 bg-[#CFA66A] text-[#0F0E0C] font-mono text-lg tracking-[0.2em] uppercase px-12 py-4 rounded-sm hover:bg-[#EBE5D9] transition-colors"
                >
                  Traded?
                </button>
                <p className="text-xs text-[#A39A8E] mt-4">
                  Tap to answer — yes logs your trades, no marks the day as no positions.
                </p>
                <button
                  data-testid="today-import-btn"
                  onClick={() => setImportOpen(true)}
                  className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#CFA66A] hover:text-[#EBE5D9] transition-colors"
                >
                  ⇪ Import from broker (TradeLocker CSV / paste)
                </button>
              </div>
            ) : (
              <div className="fade-in">
                <p className="font-head text-2xl text-[#EBE5D9]">Did you take any trades today?</p>
                <p className="text-sm text-[#A39A8E] mt-2">
                  Choose <span className="text-[#EBE5D9]">no</span> to mark the day as{" "}
                  <span className="font-mono">no positions taken</span> — sitting on your hands is also a
                  professional choice.
                </p>
                <div className="grid grid-cols-2 gap-3 mt-6">
                  <button
                    data-testid={TID.todayDidTradeYes}
                    onClick={() => startTrade()}
                    disabled={saving}
                    className="p-6 border border-[#2E2A25] bg-[#0F0E0C] rounded-sm hover:border-[#CFA66A] transition-colors text-left"
                  >
                    <p className="font-mono text-lg tracking-widest text-[#CFA66A]">YES</p>
                    <p className="text-xs text-[#A39A8E] mt-1">Log my trades &mdash; ends with account assignment</p>
                  </button>
                  <button
                    data-testid={TID.todayDidTradeNo}
                    onClick={markNoPositions}
                    disabled={saving}
                    className="p-6 border border-[#2E2A25] bg-[#0F0E0C] rounded-sm hover:border-[#5C7C8A] transition-colors text-left"
                  >
                    <p className="font-mono text-lg tracking-widest text-[#5C7C8A]">NO</p>
                    <p className="text-xs text-[#A39A8E] mt-1">Mark as no positions</p>
                  </button>
                </div>
                <button
                  data-testid="today-gate-back"
                  onClick={() => setGateStage("start")}
                  className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A39A8E] hover:text-[#EBE5D9] transition-colors"
                >
                  ← Back
                </button>
              </div>
            )}
            {error && (
              <p className="text-[#B56258] text-sm font-mono mt-4">{error}</p>
            )}
          </div>
        )}

        {/* TRADE FLOW */}
        {phase === "trade" && (
          <div className="mt-10 border border-[#2E2A25] bg-[#1A1815] p-8 rounded-sm slide-up">
            <div className="flex items-center justify-between mb-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8E]">
                Step {step + 1} of {steps.length}
                {importTotal > 0 && (
                  <span data-testid="today-import-progress" className="ml-3 text-[#CFA66A]">
                    Trade {importTotal - importQueue.length} of {importTotal} imported
                  </span>
                )}
                {presetAccountId && (
                  <span data-testid="today-preset-account" className="ml-3 text-[#CFA66A] normal-case tracking-normal">
                    → {accountLabel(presetAccountId) || "account"}
                  </span>
                )}
              </p>
              <StepDots index={step} steps={steps} />
            </div>

            <div key={step} className="fade-in">{renderStep()}</div>

            {error && (
              <p className="text-[#B56258] text-sm font-mono mt-4">{error}</p>
            )}

            <div className="flex items-center justify-between mt-8 gap-3">
              <button
                data-testid={TID.todayBackStep}
                onClick={step === 0 ? cancelWizard : back}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A39A8E] hover:text-[#EBE5D9] transition-colors"
              >
                ← {step === 0 ? "Cancel" : "Back"}
              </button>

              {step < steps.length - 1 ? (
                <button
                  data-testid={TID.todayNextStep}
                  onClick={next}
                  className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-6 py-2.5 rounded-sm hover:bg-[#EBE5D9] transition-colors"
                >
                  Continue →
                </button>
              ) : (
                <button
                  data-testid={TID.todaySaveTrade}
                  onClick={saveTrade}
                  disabled={saving}
                  className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-6 py-2.5 rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save trade"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* DONE */}
        {phase === "done" && entry && (
          <div className="mt-10 slide-up space-y-5">
            {entry.no_positions ? (
              <div
                data-testid={TID.todayNoPositionsConfirmed}
                className="border border-[#5C7C8A]/40 bg-[#5C7C8A]/5 p-8 rounded-sm text-center"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C7C8A]">
                  &mdash; Day logged
                </p>
                <p className="font-head text-3xl text-[#EBE5D9] mt-3">No positions today.</p>
                <p className="text-sm text-[#A39A8E] mt-3 max-w-md mx-auto">
                  The day is ticked off on your calendar. Choosing not to trade is a valid,
                  professional decision.
                </p>
                <button
                  onClick={resetDay}
                  className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A39A8E] hover:text-[#EBE5D9] transition-colors"
                >
                  Actually, I traded &mdash; reset
                </button>
                <button
                  data-testid="today-import-btn-nopos"
                  onClick={() => setImportOpen(true)}
                  className="mt-6 ml-6 font-mono text-[11px] uppercase tracking-[0.2em] text-[#CFA66A] hover:text-[#EBE5D9] transition-colors"
                >
                  ⇪ Import from broker
                </button>
              </div>
            ) : (
              <>
                <div className="border border-[#2E2A25] bg-[#1A1815] p-6 rounded-sm">
                  <div className="flex items-baseline justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">
                      &mdash; Day P&amp;L
                    </p>
                    <p
                      className={`font-mono text-3xl ${
                        totalToday > 0
                          ? "text-[#7A9E80]"
                          : totalToday < 0
                            ? "text-[#B56258]"
                            : "text-[#EBE5D9]"
                      }`}
                    >
                      {formatMoney(totalToday, user?.currency)}
                    </p>
                  </div>
                  <p className="text-sm text-[#A39A8E] mt-1">
                    {entry.trades.length} trade{entry.trades.length !== 1 ? "s" : ""} logged
                  </p>
                </div>

                <div className="space-y-3">
                  {entry.trades.map((t, i) => {
                    const pnl = Number(t.pnl) || 0;
                    const acc = t.account_id ? accountLabel(t.account_id) : null;
                    return (
                      <div
                        key={i}
                        className="border border-[#2E2A25] bg-[#1A1815] p-5 rounded-sm"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-mono text-sm text-[#EBE5D9]">
                              <span className="text-[#CFA66A]">{t.pair}</span>{" "}
                              <span className="text-[#A39A8E]">
                                {String(t.direction).toUpperCase()}
                              </span>{" "}
                              <span className="text-[#A39A8E]">
                                &middot; {t.lot_size} lots &middot; {t.entry} → {t.exit}
                              </span>
                            </p>
                            <p className="font-mono text-[10px] uppercase tracking-widest mt-2 text-[#A39A8E] flex items-center gap-3 flex-wrap">
                              {t.outcome && (
                                <span>
                                  Outcome: <span className={t.outcome === "win" ? "text-[#7A9E80]" : t.outcome === "loss" ? "text-[#B56258]" : "text-[#5C7C8A]"}>{t.outcome}</span>
                                </span>
                              )}
                              <span data-testid={`today-trade-account-${i}`} className={acc ? "text-[#CFA66A]" : "text-[#5C7C8A]"}>
                                {acc ? `⌂ ${acc}` : "Unassigned"}
                              </span>
                            </p>
                          </div>
                          <p
                            className={`font-mono text-xl shrink-0 ${
                              pnl > 0
                                ? "text-[#7A9E80]"
                                : pnl < 0
                                  ? "text-[#B56258]"
                                  : "text-[#EBE5D9]"
                            }`}
                          >
                            {formatMoney(pnl, user?.currency)}
                          </p>
                        </div>
                        {t.summary && (
                          <div className="mt-4 pt-4 border-t border-[#2E2A25]">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E] mb-2">
                              Your notes
                            </p>
                            <p className="text-sm text-[#EBE5D9] whitespace-pre-wrap leading-relaxed">
                              {t.summary}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <button
                    data-testid={TID.todayAddAnother}
                    onClick={() => startTrade()}
                    className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-5 py-2.5 rounded-sm hover:bg-[#EBE5D9] transition-colors"
                  >
                    + Add another trade
                  </button>
                  <button
                    data-testid="today-import-btn-done"
                    onClick={() => setImportOpen(true)}
                    className="bg-transparent border border-[#CFA66A]/50 text-[#CFA66A] hover:border-[#CFA66A] px-5 py-2.5 rounded-sm transition-colors font-mono text-xs uppercase tracking-widest"
                  >
                    ⇪ Import from broker
                  </button>
                  <button
                    data-testid={TID.todayDoneForDay}
                    onClick={() => navigate("/calendar")}
                    className="bg-transparent border border-[#2E2A25] text-[#EBE5D9] hover:bg-[#26231F] px-5 py-2.5 rounded-sm transition-colors"
                  >
                    Done for today &mdash; view calendar
                  </button>
                  <button
                    onClick={resetDay}
                    className="ml-auto font-mono text-[11px] uppercase tracking-[0.2em] text-[#8C4A42] hover:text-[#B56258] transition-colors"
                  >
                    Reset day
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ImportTradesModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        accounts={accounts}
        currency={user?.currency}
        onImport={beginImport}
      />
    </Layout>
  );
}
